/**
 * NetworkEditModeIndicator - Indicador visual de modo de edición activo
 *
 * @version 1.0
 * @created 2026-03-03
 * @module core/spatial/components
 *
 * @description
 * Badge flotante que muestra el modo de edición actual y las acciones disponibles.
 * Aparece en la esquina superior derecha del mapa cuando hay un modo activo.
 *
 * @remarks
 * **Modos soportados**:
 * - create-branch: "Creando rama - Click en el mapa"
 * - split-segment: "Dividiendo segmento"
 * - delete-node: "Eliminando nodo"
 * - delete-edge: "Eliminando segmento"
 * - null: No se muestra nada
 *
 * **Información mostrada**:
 * - Ícono del modo
 * - Mensaje descriptivo
 * - Atajos de teclado
 */

import { GitBranch, Split, Trash2, X, Move } from 'lucide-react';
import type { NetworkEditMode } from '../contexts/NetworkEditContext';
import './network-edit-mode-indicator.css';

/**
 * Props del componente
 */
export interface NetworkEditModeIndicatorProps {
  /**
   * Modo de edición activo
   */
  mode: NetworkEditMode;

  /**
   * Si se está mostrando un preview (esperando confirmación)
   */
  hasPreview: boolean;

  /**
   * Callback para cancelar operación
   */
  onCancel: () => void;
}

/**
 * Mensajes por modo de edición
 */
const MODE_MESSAGES: Record<Exclude<NetworkEditMode, null>, { title: string; action: string }> = {
  'create-branch': {
    title: 'Creando rama',
    action: 'Click en el mapa para colocar nodo',
  },
  'split-segment': {
    title: 'Dividiendo segmento',
    action: 'Confirma o cancela',
  },
  'delete-node': {
    title: 'Eliminando nodo',
    action: 'Confirma o cancela',
  },
  'delete-edge': {
    title: 'Eliminando segmento',
    action: 'Confirma o cancela',
  },
  'move-node': {
    title: 'Moviendo nodo',
    action: 'Arrastra el nodo a nueva posición',
  },
  'insert-node-on-edge': {
    title: 'Insertando nodo',
    action: 'Click en el segmento para colocar nodo',
  },
};

/**
 * Iconos por modo de edición
 */
const MODE_ICONS: Record<Exclude<NetworkEditMode, null>, React.ReactNode> = {
  'create-branch': <GitBranch size={18} />,
  'split-segment': <Split size={18} />,
  'delete-node': <Trash2 size={18} />,
  'delete-edge': <Trash2 size={18} />,
  'move-node': <Move size={18} />,
  'insert-node-on-edge': <Split size={18} />,
};

/**
 * Componente NetworkEditModeIndicator
 */
export function NetworkEditModeIndicator({
  mode,
  hasPreview,
  onCancel,
}: NetworkEditModeIndicatorProps): JSX.Element | null {
  // No mostrar si no hay modo activo
  if (!mode) return null;

  const { title, action } = MODE_MESSAGES[mode];
  const icon = MODE_ICONS[mode];

  return (
    <div className="network-edit-mode-indicator">
      {/* Ícono */}
      <div className="network-edit-mode-indicator-icon">{icon}</div>

      {/* Contenido */}
      <div className="network-edit-mode-indicator-content">
        <div className="network-edit-mode-indicator-title">{title}</div>
        <div className="network-edit-mode-indicator-action">{action}</div>
        {hasPreview && (
          <div className="network-edit-mode-indicator-shortcuts">
            <kbd>Enter</kbd> confirmar · <kbd>Esc</kbd> cancelar
          </div>
        )}
      </div>

      {/* Botón cerrar */}
      <button
        className="network-edit-mode-indicator-close"
        onClick={onCancel}
        title="Cancelar (Esc)"
      >
        <X size={16} />
      </button>
    </div>
  );
}
